import React, { useState } from 'react';
import { BiMessageSquareAdd } from 'react-icons/bi';
import {
    IoCaretDownSharp,
    IoCaretUpSharp,
    IoWaterOutline,
    IoTrashOutline
} from 'react-icons/io5';
import { PiTextUnderline } from 'react-icons/pi';
import { RiStickyNoteLine } from 'react-icons/ri';
import {
    TfiLayoutLineSolid,
    TfiLineDashed,
    TfiLineDotted
} from 'react-icons/tfi';

import './index.css';

type AnnotationHelperProps = {
    id: string;
    annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    };
    annotationColor: string;
    annotationUnderline: string;
    notesOpen: boolean;
    setAnnotationColor: (color: string) => void;
    setAnnotationUnderline: (underline: string) => void;
    setNotesOpen: (notesOpen: boolean) => void;
    setAnnotation: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => void;
    saveAnnotation: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => void;
    deleteAnnotation: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
    }) => void;
}

function AnnotationHelper({
    id,
    annotation,
    annotationColor,
    annotationUnderline,
    notesOpen,
    setAnnotationColor,
    setAnnotationUnderline,
    setNotesOpen,
    setAnnotation,
    saveAnnotation,
    deleteAnnotation
}: AnnotationHelperProps) {
    const [underlineOpen, setUnderlineOpen] = useState(false);

    const setColor = (color: string) => {
        setAnnotationColor(color);
        const updatedAnnotation = {
            ...annotation,
            color: color,
            underline: annotation.underline ? annotation.underline : annotationUnderline
        };
        saveAnnotation(updatedAnnotation);
        
        // Clear browser text selection
        if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
        
        setAnnotation(null); // Clear current annotation so it renders as saved
    }

    const setUnderline = (underline: string) => {
        setAnnotationUnderline(underline);
        const updatedAnnotation = {
            ...annotation,
            color: annotation.color ? annotation.color : annotationColor,
            underline: underline
        };
        saveAnnotation(updatedAnnotation);
        
        // Clear browser text selection
        if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
        
        setAnnotation(null); // Clear current annotation so it renders as saved
    }

    const openAnnotation = () => {
        if (annotation.annotation === null) {
            saveAnnotation({
                ...annotation,
                annotation: '',
                color: annotation.color ? annotation.color : annotationColor,
                underline: annotation.underline ? annotation.underline : annotationUnderline
            });
            setAnnotation({
                ...annotation,
                annotation: '',
                color: annotation.color ? annotation.color : annotationColor,
                underline: annotation.underline ? annotation.underline : annotationUnderline
            });
        }

        setNotesOpen(true);
    }

    return (
        <div 
            id={id} 
            className='annotationHelper'
            onMouseDown={(e) => e.preventDefault()}
        >
            <div className={annotation.color === 'Yellow'
                ? 'annotationHelper__yellow--active'
                : 'annotationHelper__yellow'
            } onClick={() => setColor('Yellow')}>
                {annotation.color === 'Yellow' && <IoWaterOutline />}
            </div>
            <div className={annotation.color === 'Pink'
                ? 'annotationHelper__pink--active'
                : 'annotationHelper__pink'    
            } onClick={() => setColor('Pink')}>
                {annotation.color === 'Pink' && <IoWaterOutline />}
            </div>
            <div className={annotation.color === 'Blue'
                ? 'annotationHelper__blue--active'
                : 'annotationHelper__blue'
            } onClick={() => setColor('Blue')}>
                {annotation.color === 'Blue' && <IoWaterOutline />}
            </div>
            <div className='annotationHelper__underline' onClick={() => setUnderlineOpen(!underlineOpen)}>
                {annotation.underline === 'Solid'
                    ? <TfiLayoutLineSolid className='annotationHelper__underlineIcon' />
                    : annotation.underline === 'Dashed'
                    ? <TfiLineDashed className='annotationHelper__underlineIcon' />
                    : annotation.underline === 'Dotted'
                    ? <TfiLineDotted className='annotationHelper__underlineIcon' />
                    : <PiTextUnderline className='annotationHelper__underlineIcon' />
                }
                {underlineOpen
                    ? <IoCaretUpSharp className='annotationHelper__underlineDropdownIcon' />
                    : <IoCaretDownSharp className='annotationHelper__underlineDropdownIcon' />
                }
                {underlineOpen &&
                    <div className='annotationHelper__underlineDropdown'>
                        <TfiLayoutLineSolid className='annotationHelper__underlineIcon' onClick={() => setUnderline('Solid')} />
                        <TfiLineDashed className='annotationHelper__underlineIcon' onClick={() => setUnderline('Dashed')} />
                        <TfiLineDotted className='annotationHelper__underlineIcon' onClick={() => setUnderline('Dotted')} />
                        <p className='annotationHelper__underlineText' onClick={() => setUnderline(null)}>None</p>
                    </div>
                }
            </div>
            <div className='annotationHelper__delete' onClick={() => {
                deleteAnnotation(annotation);
                setAnnotation(null);
            }}>
                <IoTrashOutline />
            </div>
            <div className='annotationHelper__divider' />
            <div className={annotation.annotation !== null
                ? 'annotationHelper__note--active'
                : 'annotationHelper__note'
             } onClick={openAnnotation}>
                <RiStickyNoteLine />
            </div>
        </div>
    );
}

export default AnnotationHelper;